// resumable-upload — our own resumable uploader against the Firebase
// Storage REST API, replacing `uploadBytesResumable` from the SDK.
//
// Why: the SDK's UploadTask lives only in memory. iOS Safari kills the
// in-flight request when the screen locks or the phone goes to airplane
// mode; the task then dies with `storage/unknown` and the only option was
// to start over from byte 0. Here the upload session URL is persisted
// (sessionStorage) together with a fingerprint of the File, so an upload
// can continue from the server's last received byte after a network
// drop, a screen lock, or even a page refresh (once the user re-picks the
// same file — the File object itself does not survive a refresh).
//
// Protocol (mirrors @firebase/storage's own requests, see
// createResumableUpload / getResumableUploadStatus / continueResumableUpload):
//   start   POST {base}/v0/b/{bucket}/o?name={path}
//           X-Goog-Upload-Protocol: resumable, X-Goog-Upload-Command: start,
//           X-Goog-Upload-Header-Content-Length, X-Goog-Upload-Header-Content-Type,
//           body: JSON metadata {name, contentType}
//           → header X-Goog-Upload-URL (the session URL), X-Goog-Upload-Status: active
//   upload  POST {sessionUrl}  X-Goog-Upload-Command: upload | "upload, finalize" | finalize
//           X-Goog-Upload-Offset: {offset}  body: file.slice(offset, end)
//           → X-Goog-Upload-Status: active | final (+ object metadata JSON when final)
//   query   POST {sessionUrl}  X-Goog-Upload-Command: query
//           → X-Goog-Upload-Status, X-Goog-Upload-Size-Received
// Every request carries `Authorization: Firebase <idToken>`. Chunks are
// multiples of 256 KiB (protocol requirement); we use 8 MiB.
//
// Pure module: fetch, token source and storage are injected so it is
// unit-tested in node with a fake server.

export const DEFAULT_STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com';
/** Protocol requirement: every non-final chunk is a multiple of this. */
export const RESUMABLE_GRANULE_BYTES = 256 * 1024;
export const DEFAULT_CHUNK_BYTES = 8 * 1024 * 1024;
export const DEFAULT_SESSION_STORAGE_KEY = 'nayto_resumable_upload';

export interface UploadFingerprint {
  name: string;
  size: number;
  lastModified: number;
}

export interface ResumableSessionRecord {
  uploadUrl: string;
  bucket: string;
  path: string;
  contentType: string;
  fingerprint: UploadFingerprint;
  startedAt: number;
  /** Bytes the server had after the last completed chunk (UI hint only —
   * the real offset is re-queried on resume). */
  lastOffset?: number;
}

export type UploaderState =
  | 'idle'
  | 'starting'
  | 'uploading'
  | 'paused'
  | 'interrupted'
  | 'done'
  | 'canceled'
  | 'failed';

/** Minimal File-like shape (so tests can pass a Blob with a name). */
export interface UploadSource {
  name: string;
  size: number;
  type?: string;
  lastModified?: number;
  slice(start: number, end: number): Blob;
}

export interface UploadResult {
  downloadUrl: string;
  path: string;
  bucket: string;
  metadata: Record<string, unknown>;
}

export interface ResumableUploadOptions {
  file: UploadSource;
  bucket: string;
  path: string;
  contentType?: string;
  baseUrl?: string;
  getToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  chunkBytes?: number;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  storageKey?: string;
  /** Continue an earlier session (from readResumableSession) instead of
   * starting a new one. The caller must have checked the fingerprint. */
  resumeFrom?: ResumableSessionRecord | null;
  onProgress?: (bytesTransferred: number, totalBytes: number) => void;
  onStateChange?: (state: UploaderState) => void;
}

export class ResumableUploadError extends Error {
  code: string;
  status: number | null;
  constructor(code: string, message: string, status: number | null = null) {
    super(message);
    this.name = 'ResumableUploadError';
    this.code = code;
    this.status = status;
  }
}

// ─── Fingerprint + session record helpers ────────────────────────────

export function fileFingerprint(file: UploadSource): UploadFingerprint {
  return { name: file.name, size: file.size, lastModified: file.lastModified ?? 0 };
}

export function fingerprintMatches(record: ResumableSessionRecord, file: UploadSource): boolean {
  const f = fileFingerprint(file);
  return (
    record.fingerprint.name === f.name &&
    record.fingerprint.size === f.size &&
    record.fingerprint.lastModified === f.lastModified
  );
}

export function readResumableSession(
  storage: Pick<Storage, 'getItem'> | null | undefined,
  key: string = DEFAULT_SESSION_STORAGE_KEY,
): ResumableSessionRecord | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Partial<ResumableSessionRecord>;
    if (
      typeof rec.uploadUrl !== 'string' ||
      typeof rec.path !== 'string' ||
      typeof rec.bucket !== 'string' ||
      !rec.fingerprint ||
      typeof rec.fingerprint.size !== 'number'
    ) {
      return null;
    }
    return rec as ResumableSessionRecord;
  } catch {
    return null;
  }
}

export function clearResumableSession(
  storage: Pick<Storage, 'removeItem'> | null | undefined,
  key: string = DEFAULT_SESSION_STORAGE_KEY,
): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ─── URL helpers ─────────────────────────────────────────────────────

export function objectUrl(baseUrl: string, bucket: string, path: string): string {
  return `${baseUrl}/v0/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(path)}`;
}

export function startUploadUrl(baseUrl: string, bucket: string, path: string): string {
  return `${baseUrl}/v0/b/${encodeURIComponent(bucket)}/o?name=${encodeURIComponent(path)}`;
}

/** Same shape the SDK's getDownloadURL returns: `…/o/<path>?alt=media&token=<first token>`. */
export function downloadUrlFromMetadata(
  baseUrl: string,
  bucket: string,
  path: string,
  metadata: Record<string, unknown>,
): string | null {
  const tokens = metadata['downloadTokens'];
  if (typeof tokens !== 'string' || tokens.length === 0) return null;
  const token = tokens.split(',')[0] ?? '';
  if (!token) return null;
  return `${objectUrl(baseUrl, bucket, path)}?alt=media&token=${encodeURIComponent(token)}`;
}

// ─── Uploader ────────────────────────────────────────────────────────

function isNetworkError(err: unknown): boolean {
  // fetch rejects with TypeError on network failure; AbortError when we
  // aborted on purpose (handled separately).
  return err instanceof TypeError || (err instanceof Error && err.name === 'TypeError');
}

function errorForStatus(status: number, context: string): ResumableUploadError {
  if (status === 401 || status === 403) {
    return new ResumableUploadError('storage/unauthorized', `${context}: HTTP ${status}`, status);
  }
  if (status === 404 || status === 410) {
    return new ResumableUploadError('storage/session-lost', `${context}: HTTP ${status}`, status);
  }
  if (status === 413) {
    return new ResumableUploadError('storage/quota-exceeded', `${context}: HTTP ${status}`, status);
  }
  return new ResumableUploadError('storage/unknown', `${context}: HTTP ${status}`, status);
}

export class ResumableUploader {
  private readonly file: UploadSource;
  private readonly bucket: string;
  private readonly path: string;
  private readonly contentType: string;
  private readonly baseUrl: string;
  private readonly getToken: () => Promise<string | null>;
  private readonly fetchImpl: typeof fetch;
  private readonly chunkBytes: number;
  private readonly storage: ResumableUploadOptions['storage'];
  private readonly storageKey: string;
  private readonly onProgress?: ResumableUploadOptions['onProgress'];
  private readonly onStateChange?: ResumableUploadOptions['onStateChange'];

  private uploadUrl: string | null = null;
  private offset = 0;
  private _state: UploaderState = 'idle';
  private abort: AbortController | null = null;
  private wakeResume: (() => void) | null = null;
  private canceled = false;
  private pauseRequested = false;

  constructor(opts: ResumableUploadOptions) {
    this.file = opts.file;
    this.bucket = opts.bucket;
    this.path = opts.path;
    this.contentType = opts.contentType || opts.file.type || 'application/octet-stream';
    this.baseUrl = (opts.baseUrl ?? DEFAULT_STORAGE_BASE_URL).replace(/\/+$/, '');
    this.getToken = opts.getToken;
    this.fetchImpl = opts.fetchImpl ?? ((...a) => fetch(...a));
    const chunk = opts.chunkBytes ?? DEFAULT_CHUNK_BYTES;
    // Keep the protocol's 256 KiB alignment whatever the caller passes.
    this.chunkBytes = Math.max(
      RESUMABLE_GRANULE_BYTES,
      Math.floor(chunk / RESUMABLE_GRANULE_BYTES) * RESUMABLE_GRANULE_BYTES,
    );
    this.storage = opts.storage ?? null;
    this.storageKey = opts.storageKey ?? DEFAULT_SESSION_STORAGE_KEY;
    this.onProgress = opts.onProgress;
    this.onStateChange = opts.onStateChange;
    if (opts.resumeFrom) {
      this.uploadUrl = opts.resumeFrom.uploadUrl;
    }
  }

  get state(): UploaderState {
    return this._state;
  }
  get bytesTransferred(): number {
    return this.offset;
  }
  get totalBytes(): number {
    return this.file.size;
  }
  /** The session URL — persisted so a later page load can continue. */
  get sessionUrl(): string | null {
    return this.uploadUrl;
  }

  /** Stop after the in-flight chunk is aborted; `resume()` continues from
   * the server's offset. Used when the browser goes offline. */
  pause(): void {
    if (this._state !== 'uploading' && this._state !== 'starting') return;
    this.pauseRequested = true;
    this.abort?.abort();
  }

  /** Continue after pause() or after an interruption. Safe to call when
   * nothing is waiting (no-op). */
  resume(): void {
    this.pauseRequested = false;
    const wake = this.wakeResume;
    this.wakeResume = null;
    wake?.();
  }

  /** Abort and forget the session. run() rejects with storage/canceled. */
  cancel(): void {
    this.canceled = true;
    this.abort?.abort();
    const wake = this.wakeResume;
    this.wakeResume = null;
    wake?.();
    clearResumableSession(this.storage, this.storageKey);
  }

  private setState(s: UploaderState): void {
    if (this._state === s) return;
    this._state = s;
    this.onStateChange?.(s);
  }

  private async authHeaders(extra: Record<string, string>): Promise<Headers> {
    const h = new Headers(extra);
    const token = await this.getToken();
    if (token) h.set('Authorization', `Firebase ${token}`);
    return h;
  }

  private persist(): void {
    if (!this.uploadUrl) return;
    try {
      const rec: ResumableSessionRecord = {
        uploadUrl: this.uploadUrl,
        bucket: this.bucket,
        path: this.path,
        contentType: this.contentType,
        fingerprint: fileFingerprint(this.file),
        startedAt: Date.now(),
        lastOffset: this.offset,
      };
      this.storage?.setItem(this.storageKey, JSON.stringify(rec));
    } catch {
      /* storage full / private mode — resume-after-refresh just won't work */
    }
  }

  /** Waits until resume() (or cancel()) is called. */
  private waitForResume(): Promise<void> {
    return new Promise((resolve) => {
      this.wakeResume = resolve;
    });
  }

  private async startSession(): Promise<void> {
    this.setState('starting');
    this.abort = new AbortController();
    const headers = await this.authHeaders({
      'X-Goog-Upload-Protocol': 'resumable',
      'X-Goog-Upload-Command': 'start',
      'X-Goog-Upload-Header-Content-Length': String(this.file.size),
      'X-Goog-Upload-Header-Content-Type': this.contentType,
      'Content-Type': 'application/json; charset=utf-8',
    });
    const res = await this.fetchImpl(startUploadUrl(this.baseUrl, this.bucket, this.path), {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: this.path, contentType: this.contentType }),
      signal: this.abort.signal,
    });
    if (!res.ok) throw errorForStatus(res.status, 'start');
    const url = res.headers.get('X-Goog-Upload-URL');
    const status = res.headers.get('X-Goog-Upload-Status');
    if (!url || (status && status !== 'active')) {
      throw new ResumableUploadError('storage/unknown', 'start: no upload URL in response');
    }
    this.uploadUrl = url;
    this.offset = 0;
    this.persist();
  }

  /** Ask the server how much it has. Returns {received, final}. */
  private async query(): Promise<{ received: number; final: boolean }> {
    if (!this.uploadUrl) throw new ResumableUploadError('storage/unknown', 'query without session');
    this.abort = new AbortController();
    const headers = await this.authHeaders({ 'X-Goog-Upload-Command': 'query' });
    const res = await this.fetchImpl(this.uploadUrl, {
      method: 'POST',
      headers,
      signal: this.abort.signal,
    });
    if (!res.ok) throw errorForStatus(res.status, 'query');
    const status = res.headers.get('X-Goog-Upload-Status');
    const received = Number(res.headers.get('X-Goog-Upload-Size-Received') ?? '');
    if (status !== 'active' && status !== 'final') {
      throw new ResumableUploadError('storage/session-lost', `query: status ${status ?? 'none'}`);
    }
    if (!Number.isFinite(received)) {
      throw new ResumableUploadError('storage/unknown', 'query: no size received');
    }
    return { received, final: status === 'final' };
  }

  private async fetchMetadata(): Promise<Record<string, unknown>> {
    this.abort = new AbortController();
    const headers = await this.authHeaders({});
    const res = await this.fetchImpl(objectUrl(this.baseUrl, this.bucket, this.path), {
      method: 'GET',
      headers,
      signal: this.abort.signal,
    });
    if (!res.ok) throw errorForStatus(res.status, 'metadata');
    return (await res.json()) as Record<string, unknown>;
  }

  /** Upload one chunk starting at this.offset. Returns metadata when the
   * server reports `final`, else null. */
  private async uploadChunk(): Promise<Record<string, unknown> | null> {
    if (!this.uploadUrl)
      throw new ResumableUploadError('storage/unknown', 'upload without session');
    const total = this.file.size;
    const bytesLeft = total - this.offset;
    const bytesToUpload = Math.min(bytesLeft, this.chunkBytes);
    const end = this.offset + bytesToUpload;
    const command =
      bytesToUpload === 0
        ? 'finalize'
        : bytesLeft === bytesToUpload
          ? 'upload, finalize'
          : 'upload';
    this.abort = new AbortController();
    const headers = await this.authHeaders({
      'X-Goog-Upload-Command': command,
      'X-Goog-Upload-Offset': String(this.offset),
    });
    const res = await this.fetchImpl(this.uploadUrl, {
      method: 'POST',
      headers,
      body: bytesToUpload > 0 ? this.file.slice(this.offset, end) : undefined,
      signal: this.abort.signal,
    });
    if (!res.ok) throw errorForStatus(res.status, 'upload');
    const status = res.headers.get('X-Goog-Upload-Status');
    if (status !== 'active' && status !== 'final') {
      throw new ResumableUploadError('storage/unknown', `upload: status ${status ?? 'none'}`);
    }
    this.offset = end;
    this.onProgress?.(this.offset, total);
    if (status !== 'final') this.persist();
    if (status === 'final') {
      try {
        return (await res.json()) as Record<string, unknown>;
      } catch {
        return {};
      }
    }
    return null;
  }

  /**
   * Run to completion. Network failures and pause() do not reject: the
   * uploader goes to 'interrupted' / 'paused' and waits for resume(),
   * then re-syncs the offset with a query. Only cancel() and permanent
   * server errors (401/403…) reject.
   */
  async run(): Promise<UploadResult> {
    const total = this.file.size;
    let metadata: Record<string, unknown> | null = null;
    let needSync = this.uploadUrl != null; // resuming an earlier session → ask the server first

    for (;;) {
      if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
      try {
        if (!this.uploadUrl) {
          await this.startSession();
        }
        if (needSync) {
          const q = await this.query();
          needSync = false;
          if (q.final) {
            this.offset = total;
            this.onProgress?.(total, total);
            metadata = await this.fetchMetadata();
            break;
          }
          this.offset = Math.min(q.received, total);
          this.onProgress?.(this.offset, total);
        }
        this.setState('uploading');
        while (metadata === null) {
          if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
          if (this.pauseRequested) {
            this.setState('paused');
            await this.waitForResume();
            if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
            needSync = true;
            break;
          }
          metadata = await this.uploadChunk();
          if (metadata === null && this.offset >= total) {
            // Server did not say final after the last byte: finalize explicitly.
            metadata = await this.uploadChunk();
          }
        }
        if (metadata !== null) break;
        continue; // paused → re-sync loop
      } catch (err) {
        if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
        const aborted = err instanceof Error && err.name === 'AbortError';
        if (aborted && this.pauseRequested) {
          this.setState('paused');
          await this.waitForResume();
          if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
          needSync = true;
          continue;
        }
        if (err instanceof ResumableUploadError && err.code === 'storage/session-lost') {
          // Session expired or was never valid: start a fresh one from 0.
          this.uploadUrl = null;
          this.offset = 0;
          needSync = false;
          clearResumableSession(this.storage, this.storageKey);
          continue;
        }
        if (err instanceof ResumableUploadError && err.status != null && err.status < 500) {
          this.setState('failed');
          clearResumableSession(this.storage, this.storageKey);
          throw err;
        }
        // Network failure, 5xx, or an abort we did not ask for: wait for
        // the caller to resume (online event / return to foreground /
        // tap), then re-sync with a query.
        if (isNetworkError(err) || aborted || err instanceof ResumableUploadError) {
          this.setState('interrupted');
          await this.waitForResume();
          if (this.canceled) throw new ResumableUploadError('storage/canceled', 'canceled');
          needSync = this.uploadUrl != null;
          continue;
        }
        this.setState('failed');
        throw err;
      }
    }

    const downloadUrl = downloadUrlFromMetadata(
      this.baseUrl,
      this.bucket,
      this.path,
      metadata ?? {},
    );
    if (!downloadUrl) {
      // Some responses omit downloadTokens on the final chunk; fetch them.
      const fresh = await this.fetchMetadata();
      const url = downloadUrlFromMetadata(this.baseUrl, this.bucket, this.path, fresh);
      if (!url) throw new ResumableUploadError('storage/unknown', 'no download token');
      clearResumableSession(this.storage, this.storageKey);
      this.setState('done');
      return { downloadUrl: url, path: this.path, bucket: this.bucket, metadata: fresh };
    }
    clearResumableSession(this.storage, this.storageKey);
    this.setState('done');
    return { downloadUrl, path: this.path, bucket: this.bucket, metadata: metadata ?? {} };
  }
}
